import type { PerformanceTargetComparison, UiBudgetLane, UiBudgetPoint } from '@nightshift/ui-types';

/**
 * Arithmetic behind a Budgets lane: time on x, value on y, target on the rule.
 *
 * Nightly is a line. MR is scatter. Spacing is wall-clock over the named window
 * so a multi-month series actually looks like months, not a 12-point sparkline.
 */

export interface SeriesPoint {
  outlier?: boolean;
  t: number;
  value: number;
  regression: boolean;
  noBaseline: boolean;
  machine: string;
  sha: string;
  probe: string | null;
  context: 'nightly' | 'mr';
}

interface ProbeSeries {
  name: string;
  nightly: SeriesPoint[];
  mr: SeriesPoint[];
}

export interface PerformanceSeries {
  outlierCount: number;
  probes: ProbeSeries[];
  domain: [number, number];
  window: [number, number];
  target: number | null;
  /** True when the failing side is above the rule (`lt` / `lte`). */
  ceiling: boolean;
  step: number;
}

export function niceStep(span: number, targetCount: number): number {
  if (!Number.isFinite(span) || span <= 0 || targetCount <= 0) return 1;
  const rough = span / targetCount;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const scaled = rough / magnitude;
  const step = scaled >= Math.sqrt(50) ? 10 : scaled >= Math.sqrt(10) ? 5 : scaled >= Math.sqrt(2) ? 2 : 1;
  return step * magnitude;
}

function isCeiling(comparison: PerformanceTargetComparison | null): boolean {
  return comparison === 'lt' || comparison === 'lte';
}

function padDomain(values: number[], target: number | null): [number, number] {
  const pool = target === null ? values : [...values, target];
  if (pool.length === 0) return [0, 1];
  let min = Math.min(...pool);
  let max = Math.max(...pool);
  if (max === min) {
    const pad = Math.abs(max) > 0 ? Math.abs(max) * 0.1 : 1;
    min -= pad;
    max += pad;
  } else {
    const pad = (max - min) * 0.1;
    min -= pad;
    max += pad;
  }
  if (min < 0 && Math.min(...pool) >= 0) min = 0;
  return [min, max];
}

/** Carry the raw sample through; the chart's scales decide where it lands. */
function place(point: UiBudgetPoint): SeriesPoint {
  return {
    t: point.t,
    value: point.value,
    regression: point.regression,
    noBaseline: point.noBaseline,
    machine: point.machine,
    sha: point.sha,
    probe: point.probe,
    context: point.context,
  };
}

export function performanceSeries(
  lane: UiBudgetLane,
  window: { from: number; to: number },
  probeName?: string | null,
  focusOutliers = false,
): PerformanceSeries {
  const probes = probeName
    ? lane.probes.filter((probe) => probe.name === probeName)
    : lane.probes;
  const values: number[] = [];
  const unusual = new Set<UiBudgetPoint>();
  for (const probe of probes) {
    // Compare like hardware and context; fast machines are not statistical anomalies.
    const groups = new Map<string, UiBudgetPoint[]>();
    for (const point of [...probe.nightly, ...probe.mr]) {
      const key = `${point.machine}:${point.context}`;
      const group = groups.get(key) ?? [];
      group.push(point);
      groups.set(key, group);
    }
    for (const points of groups.values()) {
      const sorted = points.map((point) => point.value).sort((a, b) => a - b);
      if (sorted.length < 5) continue;
      const q1 = sorted[Math.floor((sorted.length - 1) * 0.25)]!;
      const q3 = sorted[Math.ceil((sorted.length - 1) * 0.75)]!;
      const spread = Math.max(q3 - q1, Math.abs((q1 + q3) / 2) * 0.05, 0.001);
      for (const point of points) {
        if (point.value < q1 - 3 * spread || point.value > q3 + 3 * spread) unusual.add(point);
      }
    }
    for (const point of probe.nightly) values.push(point.value);
    for (const point of probe.mr) values.push(point.value);
  }
  const target = lane.target?.value ?? null;
  const typical = probes.flatMap((probe) => [...probe.nightly, ...probe.mr]).filter((point) => !unusual.has(point)).map((point) => point.value);
  const [min, max] = padDomain(focusOutliers && typical.length ? typical : values, target);
  const from = window.from;
  const to = window.to;

  const positioned = (point: UiBudgetPoint): SeriesPoint => ({
    ...place(point),
    outlier: unusual.has(point),
  });
  return {
    outlierCount: unusual.size,
    probes: probes.map((probe) => ({
      name: probe.name,
      nightly: probe.nightly.map(positioned),
      mr: probe.mr.map(positioned),
    })),
    domain: [min, max],
    window: [from, to],
    target,
    ceiling: isCeiling(lane.target?.comparison ?? null),
    step: niceStep(Math.max(max - min, 0), 4),
  };
}

export function axisDateLabel(at: number): string {
  return new Date(at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}
